if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        const idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) return;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.captureSubmitOriginalLabel();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        // The v2 PDP buy-button doesn't include a .loading__spinner node, so
        // querySelector returns null. Null-check before touching classList
        // — without this, ATC throws and the cart drawer never opens, which
        // looks to the user like the click did nothing.
        const submitSpinner = this.querySelector('.loading__spinner');
        if (submitSpinner) submitSpinner.classList.remove('hidden');
        this.setButtonLabel(this.submitButton.dataset.labelAdding || 'Adding…');

        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/javascript' } };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          try {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          } catch (e) {
            // If the cart element exists but its API isn't ready, fall through
            // — the add still works, the cart drawer just won't re-render.
          }
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (typeof publish === 'function') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              this.handleErrorMessage(response.description);
              this.showToast({ kind: 'error', message: response.description || response.message || 'Couldn\'t add that to your cart.' });

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) {
                this.error = true;
                return;
              }
              this.submitButton.setAttribute('aria-disabled', true);
              const labelSpan = this.submitButton.querySelector('span');
              if (labelSpan) labelSpan.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Success path. Always give the buyer visible feedback before
            // anything else — the cart drawer/notification render is best-
            // effort and shouldn't gate the confirmation animation.
            this.showSuccessAnimation(response);

            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (this.cart && typeof this.cart.renderContents === 'function') this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (this.cart && typeof this.cart.renderContents === 'function') {
              this.cart.renderContents(response);
            }
            // No cart drawer / notification element on the page — we've
            // already shown the toast; don't hard-navigate away from the
            // PDP, that's the silent-failure UX the audit called out.
          })
          .catch((e) => {
            console.error('[product-form] add to cart failed', e);
            this.showToast({ kind: 'error', message: 'Network error — couldn\'t reach the cart. Try again?' });
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            const finallySpinner = this.querySelector('.loading__spinner');
            if (finallySpinner) finallySpinner.classList.add('hidden');
            if (!this.error) {
              // Let the success label sit for ~1.8s, then restore the
              // original button text.
              window.setTimeout(() => this.restoreSubmitLabel(), 1800);
            } else {
              this.restoreSubmitLabel();
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // ---- Success animation + toast helpers --------------------------

      captureSubmitOriginalLabel() {
        if (this._submitOriginalHTML) return;
        this._submitOriginalHTML = this.submitButton.innerHTML;
      }

      setButtonLabel(html) {
        if (!this.submitButton) return;
        this.submitButton.innerHTML = html;
      }

      restoreSubmitLabel() {
        if (!this.submitButton || !this._submitOriginalHTML) return;
        this.submitButton.innerHTML = this._submitOriginalHTML;
        this.submitButton.classList.remove('is-added');
      }

      showSuccessAnimation(response) {
        if (!this.submitButton) return;
        this.submitButton.classList.add('is-added');
        const checkSvg =
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="margin-right:8px;vertical-align:-3px;"><path d="M20 6L9 17l-5-5"/></svg>';
        this.setButtonLabel(checkSvg + 'Added to cart');

        // Toast confirmation — survives even when cart drawer isn't on
        // the page (e.g. cart_type = page).
        const title = (response && response.title) || (response && response.product_title) || 'Item';
        this.showToast({ kind: 'success', message: `${title} added to your cart.` });
      }

      showToast({ kind = 'success', message = '' } = {}) {
        if (!message) return;
        let host = document.getElementById('mti-atc-toast-host');
        if (!host) {
          host = document.createElement('div');
          host.id = 'mti-atc-toast-host';
          host.setAttribute('aria-live', 'polite');
          host.setAttribute('aria-atomic', 'true');
          document.body.appendChild(host);
        }
        const toast = document.createElement('div');
        toast.className = 'mti-atc-toast mti-atc-toast--' + kind;
        const icon =
          kind === 'success'
            ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>'
            : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
        toast.innerHTML =
          '<span class="mti-atc-toast__icon">' +
          icon +
          '</span><span class="mti-atc-toast__msg">' +
          message
            .toString()
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;') +
          '</span>' +
          '<a class="mti-atc-toast__cta" href="/cart">View cart →</a>';
        host.appendChild(toast);
        // Force layout, then animate in.
        // eslint-disable-next-line no-unused-expressions
        toast.offsetHeight;
        toast.classList.add('is-visible');
        const remove = () => {
          toast.classList.remove('is-visible');
          window.setTimeout(() => {
            if (toast.parentNode) toast.parentNode.removeChild(toast);
          }, 280);
        };
        window.setTimeout(remove, 3800);
      }
    }
  );
}
